import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import styles from './MonitorQrCode.module.css';

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

/**
 * Scannable link to the MONITOR screen, so a second device (iPad / TV browser)
 * can open it without anyone typing a LAN IP.
 *
 * A browser cannot discover its machine's own wifi address, so encoding
 * window.location.origin would hand out http://localhost:5173/monitor — which no
 * other device can reach. Instead we ask the server (which does know) via
 * /__lan-info, served in both LAN-mode launchers. We fall back to the page origin
 * only when that endpoint is absent (e.g. the cloud-hosted https site).
 */
export default function MonitorQrCode({ onClose }) {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [lanUrl, setLanUrl] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/__lan-info', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => { if (!cancelled) setLanUrl(info?.lanUrl ?? null); })
      .catch(() => { /* endpoint absent — fall back to origin */ })
      .finally(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, []);

  const base = lanUrl ?? window.location.origin;
  const monitorUrl = `${base}/monitor`;
  // Unreachable from another device: no LAN address AND we're on a local origin.
  const isLocalOnly = !lanUrl && LOCAL_HOSTS.includes(window.location.hostname);
  // The server told us the LAN address even though the browser is on localhost.
  const usingLanOverride = !!lanUrl && lanUrl !== window.location.origin;

  useEffect(() => {
    if (!checked || !canvasRef.current) return;   // wait: don't paint a stale localhost QR
    QRCode.toCanvas(canvasRef.current, monitorUrl, {
      width: 208,
      margin: 1,
      color: { dark: '#0a0a0f', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    }).catch(() => { /* falls back to the printed URL below */ });
  }, [monitorUrl, checked]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(monitorUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the URL is on screen anyway */ }
  };

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <span className={styles.title}>Open the Monitor screen</span>
          <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <p className={styles.lead}>
          Point the other device&rsquo;s camera at this code to open the patient monitor.
        </p>

        <div className={styles.qrBox}>
          {checked
            ? <canvas ref={canvasRef} className={styles.qr} />
            : <div className={styles.qrLoading}>Finding wifi address…</div>}
        </div>

        <code className={styles.url}>{monitorUrl}</code>

        <div className={styles.actions}>
          <button className={styles.btn} onClick={copy}>{copied ? 'Copied ✓' : 'Copy link'}</button>
          <a className={styles.btn} href="/monitor" target="_blank" rel="noopener noreferrer">Open here ↗</a>
        </div>

        {usingLanOverride && (
          <p className={styles.ok}>
            ✓ This is this computer&rsquo;s <b>wifi address</b> — scannable from another device on
            the same network, even though this window is on localhost.
          </p>
        )}

        {isLocalOnly && (
          <p className={styles.warn}>
            ⚠ This is a <b>localhost</b> address — only this computer can open it. For a second
            device, start the sim in two-device mode (double-click <b>Start LAN Mode</b>) and
            reopen this panel; the code will then point at this machine&rsquo;s wifi address.
          </p>
        )}
      </div>
    </div>
  );
}
